<template>
  <div class="flex">
    <BaseTheButtonNew
      v-for="button in getCurrentButtons"
      :key="`social-button-${button.id}`"
      is-small
      is-social
      :text="button.text"
      :icon-only="iconOnly || isNotDesktop"
      :icon="button.icon"
      :type="getButtonType"
      :class="{ 'w-32': isNotMobile && !iconOnly }"
      class="mr-2 last:mr-0 xl:mr-3"
      @click="handleClick(button.type)"
    />
  </div>
</template>

<script setup>
import api from '@/services/api/api';

const props = defineProps({
  iconOnly: {
    type: Boolean,
    default: false,
  },

  darkBorder: {
    type: Boolean,
    default: false,
  },

  // непрозрачность
  isOpaque: {
    type: Boolean,
    default: false,
  },

  buttons: {
    type: Array,
    default: () => ['like', 'bookmark', 'share'],
    validate: (value) =>
      ['like', 'bookmark', 'share', 'download-pdf'].includes(value),
  },

  data: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['share', 'download-pdf']);

const authStore = useAuthStore();
const modalsStore = useModalsStore();
const { isNotMobile } = useBreakpoint();
const toast = useToast();

const localIsLiked = ref(false);
const localIsBookmarked = ref(false);

const getButtonType = computed(() => {
  if (props.isOpaque) {
    return 'default';
  } else if (props.darkBorder) {
    return 'inverse--dark-border';
  } else {
    return 'inverse';
  }
});

const getAllButtons = computed(() => {
  return [
    {
      id: 0,
      type: 'like',
      text: 'Мне нравится',
      icon: localIsLiked.value ? 'like-filled' : 'like',
    },
    {
      id: 1,
      type: 'bookmark',
      text: 'В закладки',
      icon: localIsBookmarked.value ? 'bookmark-filled' : 'bookmark',
    },
    { id: 2, type: 'share', text: 'Поделиться', icon: 'share' },
    {
      id: 3,
      type: 'download-pdf',
      text: 'Скачать PDF',
      icon: 'file-arrow-down',
    },
  ];
});

const getCurrentButtons = computed(() =>
  getAllButtons.value.filter((el) => props.buttons.includes(el.type)),
);

onMounted(() => {
  if (authStore.isAuthorized) {
    localIsLiked.value = props.data?.has_like;
    localIsBookmarked.value = props.data?.has_bookmark;
  } else {
    const userLikesString = localStorage.getItem('likes');
    const userLikesParsed = JSON.parse(userLikesString) || [];
    localIsLiked.value = userLikesParsed.includes(props.data?.id);
  }
});

const handleLike = async () => {
  const userLikesString = localStorage.getItem('likes');
  // в результате JSON.parse получаем массив id лайкнутых пользователем сущностей
  const userLikesParsed = JSON.parse(userLikesString) || [];
  const isLiked = userLikesParsed.indexOf(props.data?.id) + 1;

  const like = async () => {
    try {
      await api.socials.handleLike({
        model_type: props.data?.model_type,
        id: props.data?.id,
      });

      if (!userLikesString) {
        localStorage.setItem('likes', JSON.stringify([props.data?.id]));
      } else if (!isLiked) {
        userLikesParsed.push(props.data?.id);
        localStorage.setItem('likes', JSON.stringify(userLikesParsed));
      }

      localIsLiked.value = true;
    } catch (e) {
      console.log(e);
    }
  };

  const dislike = async () => {
    try {
      await api.socials.handleDisLike({
        model_type: props.data?.model_type,
        id: props.data?.id,
      });
      if (userLikesString && isLiked) {
        userLikesParsed.splice(isLiked - 1, 1);
        localStorage.setItem('likes', JSON.stringify(userLikesParsed));
      }

      localIsLiked.value = false;
    } catch (e) {
      console.log(e);
    }
  };

  localIsLiked.value ? await dislike() : await like();
};

const handleBookmark = async () => {
  try {
    await api.socials.handleBookmark({
      model_type: props.data?.model_type,
      id: props.data?.id,
    });
    localIsBookmarked.value = !localIsBookmarked.value;
  } catch (e) {
    toast.error(e);
  }
};

const handleClick = async (type) => {
  if (!authStore.isAuthorized && type === 'bookmark') {
    modalsStore.showSocialAuthForm();
    return;
  }

  switch (type) {
    case 'bookmark':
      await handleBookmark();
      break;
    case 'like':
      await handleLike();
      break;
    case 'share':
      emit('share');
      break;
    case 'download-pdf':
      emit('download-pdf');
      break;
    default:
  }
};
</script>

<style lang="scss"></style>
